/**
 * usePageNode — platform-agnostic model for a PageNode (the per-page xyflow
 * node). Extracted from PageNode so the web (`PageNode.tsx`) and native
 * (`PageNode.native.tsx`) renders share every bit of logic — the y-grid
 * bindings, section seeding/reconciliation, zoom-preview gating, rename state,
 * and the drop/nest-target derivations — and differ ONLY in JSX + interaction
 * affordances (web: double-click + raw <input> rename; native: menu-driven).
 *
 * Pure logic, no JSX: moving it here leaves the web render byte-for-byte
 * unchanged (the component just consumes this hook).
 */
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useStoreApi, useReactFlow, type NodeProps } from "./xyflow.js";
import {
  useGridStore,
  resetGridStore,
  type GridConfig,
} from "@fredericrous/grid";
import { bindYjsToGrid } from "@website-builder/y-grid";
import { seedPageOnce } from "./pageSeeding.js";
import { useEditorContext } from "./EditorContext.js";
import { useReadOnly } from "./ReadOnlyContext.js";
import { WIREFRAME_MAX_ZOOM, EDIT_MIN_ZOOM } from "./canvasZoom.js";
import { useAddDrag } from "./AddDragContext.js";
import { useSitemapInteraction } from "./SitemapInteractionContext.js";
import { breakpointWidth, type Breakpoint } from "./breakpoints.js";
import { makeRenderWidget } from "./widgets/renderWidget.js";
import { crossGridAccepts, inlineRowIn } from "./crossGridAccepts.js";
import { INLINE_GRID, nestedContainers, syncInlineRows } from "./inlineRows.js";
import {
  insertSectionIntoGrid,
  inlineNestedConfig,
  SECTION_NESTED_CONFIG,
  SECTION_CHROME_PX,
} from "./sectionInsert.js";
import { useElementBounds } from "./useElementBounds.js";
import { EDITOR_ROW_HEIGHT_PX } from "@website-builder/schemas";

// Three level-of-detail bands, keyed on the canvas ZOOM (element detail level),
// NOT the page's absolute on-screen width — so a narrow mobile page at ~1:1 is
// "edit", not "far-zoom wireframe" the way an absolute-px threshold (tuned for
// the 1440 desktop width) would misclassify it. The zoom cutoffs are the old
// px thresholds ÷ the desktop width, so desktop behaviour is unchanged.
//   • wireframe — zoomed far out ⇒ a cheap structural skeleton (PageSkeleton):
//     regions + columns + placeholder bars. Also a perf win (no real grids).
//   • mockup    — the REAL page renders, non-interactive (read-only preview).
//   • edit      — the real page, fully editable (near 1:1).
// Single source of truth in canvasZoom.ts (shared with the Add palette).
export type PageLod = "wireframe" | "mockup" | "edit";

/** Default section seeded into a brand-new page. */

// Default config for new page grids. Satisfies Omit<GridConfig, 'id'>
// — grid 0.9: breakpoints ([]) and reorder (true) fall back to the defaults.
const PAGE_ROW_HEIGHT_PX = EDITOR_ROW_HEIGHT_PX;
const PAGE_GRID_CONFIG: Omit<GridConfig, "id"> = {
  columns: 12,
  rowHeight: PAGE_ROW_HEIGHT_PX,
  gap: 8,
  padding: 8,
  compactType: "vertical",
  // grid 0.8: the page canvas uses the gated (velocity/dwell) drag strategy —
  // section blocks are big, so fast sweeps shouldn't reflow the whole page.
  // Inner section grids (SECTION_GRID_CONFIG) stay on the default live mode.
  dragStrategy: "gated",
};

// grid 0.8: app-level interaction wiring is registered ONCE on the
// module-singleton store.
//
// - tuning: gated-strategy overrides. vFast* defaults assume ~100px cells;
//   scale them to the page grid's actual row height so "fast" means the same
//   number of rows per second.
// - autoGrow: the store owns the section auto-grow chain — the LIVE grow
//   preview during widget drag/resize, the commit-time fixpoint, and the
//   programmatic re-fit on addItem/removeItem/resizeItem (a leaf widget's
//   useAutoGrowItem report). Parentage (section grid → page grid) is stamped
//   onto each nested GridState by usePageNode's reconciliation effect below;
//   page grids have no parent, so the walk stops there.
//
// MUST be re-applied after every `resetGridStore()`: a factory reset restores
// the interactions config to the store's initial (empty) one, and an
// unregistered `autoGrow` makes the grow chain a silent no-op — sections stop
// growing to fit their content, so a dropped widget lands outside its section
// and text overflows its tile. Call `resetGridStoreForDraft()` (below) instead
// of the raw reset so the two can never drift apart again.
export function configureGridInteractions(): void {
  useGridStore.getState().configureInteractions({
    tuning: {
      tDwellMs: 120,
      vFastEnter: 900 * (PAGE_ROW_HEIGHT_PX / 100),
      vFastExit: 500 * (PAGE_ROW_HEIGHT_PX / 100),
    },
    autoGrow: {
      parentOf: (gid) => useGridStore.getState().grids[gid]?.parentGridId,
      colsOf: (gid) => useGridStore.getState().grids[gid]?.config.columns ?? 12,
      // A section's header band (SectionWidget chrome) is a fixed px strip,
      // not a whole row — so do the real px math: nested-grid content px plus
      // chrome, ceiled into the page grid's row unit (MIGRATION.md's
      // containerRowsFor contract).
      containerRowsFor: (childGridId, contentRows) => {
        const grids = useGridStore.getState().grids;
        const child = grids[childGridId];
        const parent = child?.parentGridId
          ? grids[child.parentGridId]
          : undefined;
        const cc = child?.config;
        const pc = parent?.config;
        const crh =
          typeof cc?.rowHeight === "number" ? cc.rowHeight : PAGE_ROW_HEIGHT_PX;
        const cgap = cc?.gap ?? 8;
        const cpad = cc?.padding ?? 8;
        const prh =
          typeof pc?.rowHeight === "number" ? pc.rowHeight : PAGE_ROW_HEIGHT_PX;
        const pgap = pc?.gap ?? 8;
        const contentPx =
          cpad * 2 + contentRows * crh + Math.max(0, contentRows - 1) * cgap;
        // An Inline host has no section chrome around its row.
        const hostItem = child?.parentItemId
          ? parent?.items[child.parentItemId]
          : undefined;
        const chrome =
          hostItem?.contentType === INLINE_GRID ? 0 : SECTION_CHROME_PX;
        return Math.max(
          1,
          Math.ceil((contentPx + chrome + pgap) / (prh + pgap)),
        );
      },
    },
    // Cross-grid membership gate (grid 0.8.42) — see crossGridAccepts for the
    // rules (page bodies hold sections, section grids hold leaf widgets, shared
    // bands take nothing). Extracted + unit-tested there.
    accepts: (item, _fromGridId, toGridId) =>
      crossGridAccepts(
        item,
        toGridId,
        (gid) => useGridStore.getState().grids[gid]?.parentGridId,
        inlineRowIn(useGridStore.getState().grids),
      ),
  });
  // An Inline's grid follows its item (columns, minW) and justify.
  syncInlineRows();
}

configureGridInteractions();

/**
 * Factory-reset the singleton store for a draft swap, then RE-REGISTER the
 * interactions config the reset just cleared. Always use this rather than
 * `resetGridStore()` directly — see configureGridInteractions above.
 */
export function resetGridStoreForDraft(): void {
  resetGridStore();
  configureGridInteractions();
}

// Desktop authoring width. The page grid measures the node body and lays widgets
// out at THIS width (xyflow scales the visual via zoom — the layout itself is
// zoom-independent), so authors build real full-width responsive pages instead of
// a cramped 320px box. It ADAPTS to the user's screen — the available editor width,
// capped at a sane desktop content max — so a 1792px screen authors at ~1440 and a
// small laptop authors narrower. (Phase 2 adds tablet/mobile breakpoints.)
//
// PAGE_WIDTH is the cap / SSR + layout fallback; `useAuthoringWidth` is the live
// value used for the node body + the LOD threshold.
export const PAGE_WIDTH = 1440;
export const PAGE_HEIGHT = 480;

/** Live authoring width for the active breakpoint: desktop = min(editor viewport
 *  width, PAGE_WIDTH cap) reactive to window resize; mobile = the fixed device
 *  width. Same value for every page (one target at a time). Native-safe: React
 *  Native lacks window.innerWidth / resize, so it falls back to the PAGE_WIDTH
 *  constant (this hook is shared via usePageNode). */
export function useAuthoringWidth(breakpoint: Breakpoint = "desktop"): number {
  const read = () =>
    typeof window !== "undefined" && typeof window.innerWidth === "number"
      ? window.innerWidth
      : PAGE_WIDTH;
  const [viewport, setViewport] = useState(read);
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.addEventListener !== "function"
    )
      return;
    const onResize = () => setViewport(read());
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return breakpointWidth(breakpoint, viewport);
}

export interface PageNodeData extends Record<string, unknown> {
  label: string;
  /** Optional URL-slug OVERRIDE. Absent → the published slug is derived from
   *  `label` (serializeSiteDoc). Set via the ⋯ "Change URL…" action. */
  slug?: string;
  /** Section-template ids to seed into a brand-new page (set when the page
   *  is created from a page template; `[]` = a deliberately blank page).
   *  Only consumed once, when the page's grid is still empty. */
  seedSections?: string[];
}

/**
 * Reactive canvas zoom that re-renders the caller ONLY when the zoom changes —
 * NOT on pan. `useViewport()` returns {x,y,zoom} and re-renders on any of them,
 * so every page node would re-render on every pan frame; LOD + the nest badge
 * only care about zoom. useSyncExternalStore over the xyflow store returns the
 * zoom scalar, so React bails out of the re-render when only x/y moved.
 */
function useCanvasZoom(): number {
  const store = useStoreApi();
  return useSyncExternalStore(
    store.subscribe,
    () => store.getState().transform[2],
  );
}

export function usePageNode({ id, data }: Pick<NodeProps, "id" | "data">) {
  const { ydoc, undoManager } = useEditorContext();
  const zoom = useCanvasZoom();
  const readOnly = useReadOnly();
  // The device the canvas is authoring for (desktop/mobile) drives the page
  // width + device chrome. Read early so authoringWidth reflects it.
  const { breakpoint } = useSitemapInteraction();
  const authoringWidth = useAuthoringWidth(breakpoint);
  // Canvas zoom (element detail level) drives the LOD band — see thresholds
  // above; zoom-keyed so it's correct at any authoring width (desktop/mobile).
  const lod: PageLod = readOnly
    ? "mockup"
    : zoom < WIREFRAME_MAX_ZOOM
      ? "wireframe"
      : zoom < EDIT_MIN_ZOOM
        ? "mockup"
        : "edit";
  // WYSIWYG: widgets render their REAL content and are editable whenever the
  // page is shown for authoring — the ONLY non-editable state is an actual
  // read-only preview. (Previously the 'mockup' zoom band forced widgets
  // non-interactive with placeholder chrome; that's gone — the far-zoom
  // 'wireframe' band, which mounts the cheap SVG skeleton instead of the grid,
  // is what keeps zoomed-out canvases light.)
  const widgetPreview = readOnly;
  // The PAGE grid (section blocks) stays interactive at EVERY zoom band unless
  // read-only — so sections reorder and drag-add previews work at any zoom,
  // including the far-zoom 'wireframe' band. The far-zoom perf win now comes
  // from each SectionWidget dropping its CONTENT to a cheap SVG wireframe at
  // that band (via PageLodContext), NOT from replacing the whole grid with a
  // static skeleton (user report 2026-07-21: couldn't reorder/add in mock view).
  const pagePreview = readOnly;

  // Which widget on this page is in inline edit mode (tap-to-edit), if any.
  // Transient render-only — provided to the grid subtree via PageEditContext so
  // a text widget can announce it's editing; the page then forces the grid into
  // preview to disable item-drag while text is selected/scrolled (see
  // PageEditContext). Never persisted to Yjs.
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  // Only the PAGE-drag fields remain: a section/widget dragged from the Add
  // palette now lands via the grid engine's external-add (its own landing
  // preview through the mounted grid), so the old bespoke body dropzone +
  // section marker are gone (that "extra dropzone with a different preview" was
  // the exact thing being unified away — user report 2026-07-21).
  const { overNodeId, pageNestMode, dragging } = useAddDrag();

  // Inline rename of the page title (dbl-click → edit → updateNodeData,
  // mirrored to the Yjs nodes map by bindYjsToFlow).
  const { updateNodeData } = useReactFlow();
  const [editingLabel, setEditingLabel] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");
  const label = String(data?.["label"] ?? id);
  const slugOverride = (data as PageNodeData | undefined)?.slug;
  function startRename() {
    if (readOnly) return;
    setDraftLabel(label);
    setEditingLabel(true);
  }

  // Node action menu (right-click or the ⋯ button). Rename is triggered in-node
  // by the pencil next to the page name (startRename), not from this menu.
  const { openNodeMenu, openSlugEditor, dropParentId, dropMode } =
    useSitemapInteraction();
  // Measure the ⋯ trigger to anchor the action menu, via a cross-target seam
  // (web: getBoundingClientRect; native: measureInWindow) instead of reading
  // the DOM rect off the click event directly.
  const menuButton = useElementBounds<HTMLButtonElement>();
  // Highlight as a nest target for BOTH gestures: dragging an existing page onto
  // this one (dropParentId, R2) and dragging a new page tile from the Add
  // palette onto this one (AddDrag overNodeId while a "page" tile is dragged).
  const isNestTarget =
    dropParentId === id || (dragging?.kind === "page" && overNodeId === id);
  // Which end to highlight. Existing-page drag (R2): the top/bottom half decides
  // parent vs child (dropMode). New page from the palette: the top 20% zone means
  // "parent" (AddDrag pageNestMode), else child.
  const nestMode: "parent" | "child" =
    dropParentId === id
      ? dropMode
      : dragging?.kind === "page" && overNodeId === id
        ? (pageNestMode ?? "child")
        : "child";
  // The nest badge lives inside the node, which xyflow scales by `zoom` — so at
  // far zoom it'd shrink to unreadable. Counter-scale by 1/zoom (clamped) to keep
  // it a roughly constant on-screen size, growing as you zoom out. (mvp-first
  // does the same with its "Drop Parent/Child" label.)
  const nestBadgeScale = Math.min(6, Math.max(1, 1 / zoom));
  function commitRename() {
    const next = draftLabel.trim();
    if (next) updateNodeData(id, { label: next });
    setEditingLabel(false);
  }

  const bindingRef = useRef<ReturnType<typeof bindYjsToGrid> | null>(null);
  // PageNode owns every section's nested-grid binding (keyed by nested
  // grid id), reconciled below as section blocks come and go.
  const nestedBindingsRef = useRef<
    Map<string, ReturnType<typeof bindYjsToGrid>>
  >(new Map());

  // Stable key of this page's current section nested-grid ids (sections
  // are full-width contentType:"grid" blocks). Drives binding reconciliation.
  // Nested grids sit at EVERY depth (page → section → Inline), so the key
  // walks the tree: one `nested|hostGrid|hostItem|hostType` entry per grid.
  const sectionIdsKey = useGridStore((s) =>
    nestedContainers(s.grids, id)
      .map(
        (c) =>
          `${c.nestedGridId}|${c.hostGridId}|${c.hostItemId}|${c.contentType}`,
      )
      .sort()
      .join(","),
  );

  // Initialise this page's grid + bind y-grid. Order matters for the
  // OPFS-persistence reload path:
  //
  //   1. Create the grid SHELL (config, no items) if missing — the
  //      y-grid late-joiner only imports persisted items into a grid
  //      that already exists (it needs the config).
  //   2. Bind y-grid: if the (replayed) Y.Doc already holds this grid's
  //      items, they're imported now — synchronously, before paint.
  //   3. Seed the welcome item ONLY if the grid is still empty after the
  //      import (a genuinely new page). Seeding unconditionally would
  //      mount the text widget with empty content first, then swap in
  //      the persisted state — and lexical applies `editorState` once at
  //      mount, so the restored text would never appear.
  useEffect(() => {
    // Read-only version preview: the grid store is pre-seeded from the
    // version snapshot (exact config + items), so skip shell creation,
    // the y-grid binding, and welcome-item seeding entirely. Seeding here
    // would mutate the throwaway preview doc and make intentionally-empty
    // pages render synthetic content.
    if (readOnly) return;

    // Create the grid SHELL (config, no items) if missing — the y-grid
    // late-joiner only imports into a grid that already exists (it needs the
    // config). Cross-draft hygiene is no longer this effect's job: the
    // singleton store is factory-reset ONCE per draft open (resetGridStore at
    // the EditorScreen call sites, web + native) before any binding mounts,
    // so a pre-existing entry here can only belong to THIS draft (e.g. a
    // same-draft canvas remount) and is safe to reuse.
    if (!useGridStore.getState().grids[id]) {
      useGridStore
        .getState()
        .importLayout(id, { config: PAGE_GRID_CONFIG, items: [] });
    }

    // Bind this page's grid to the shared Yjs doc (imports persisted
    // items into the empty shell created above, if any).
    bindingRef.current = bindYjsToGrid(ydoc, id);
    const grid = useGridStore.getState().grids[id];
    // Seed a brand-new page ONCE: the template list it was created with
    // (data.seedSections, [] for a blank page), else the default section —
    // then mark it seeded, so a page emptied later stays empty (see
    // pageSeeding.ts). The page-grid binding mirrors store→Yjs; the
    // reconciliation effect binds nested grids.
    seedPageOnce(
      ydoc,
      id,
      !!grid && Object.keys(grid.items).length === 0,
      (templates) =>
        // Deterministic id base per (page, ordinal) → an idempotent seed: two
        // contexts seeding this fresh page concurrently produce identical ids
        // and CRDT-converge to one set instead of a doubled sitemap (see
        // insertSectionIntoGrid + the follower-fallback in useEditorDoc).
        templates.forEach((templateId, i) =>
          insertSectionIntoGrid(id, templateId, undefined, `${id}-seed${i}`),
        ),
    );
    // Track this grid for undo AFTER seeding (which writes synchronously), so
    // moving/resizing widgets + section blocks is undoable but the initial seed
    // isn't — the UndoManager starts with only nodes+edges in scope.
    undoManager.addToScope(ydoc.getMap(`grid:${id}`));

    return () => {
      bindingRef.current?.destroy();
      bindingRef.current = null;
    };
    // id and ydoc are stable for the lifetime of this node; readOnly is
    // fixed per canvas instance (live vs preview never swap in place).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ydoc, readOnly]);

  // Reconcile nested-grid bindings to the current section blocks. New
  // section → ensure its nested-grid shell exists (so the late-joiner can
  // import on reload) then bind it; removed section → destroy its binding.
  // Skipped in read-only (version preview): the store is pre-seeded from
  // the snapshot and the preview doc is throwaway.
  useEffect(() => {
    if (readOnly) return;
    const entries = sectionIdsKey
      ? sectionIdsKey.split(",").map((e) => {
          const [nid, hostGridId, hostItemId, hostType] = e.split("|");
          return {
            nid: nid!,
            hostGridId: hostGridId!,
            hostItemId: hostItemId!,
            hostType,
          };
        })
      : [];
    const wantedSet = new Set(entries.map((e) => e.nid));
    const map = nestedBindingsRef.current;
    // The container item hosting each nested grid, at any depth — the store's
    // parent back-reference (stamped below) so grid 0.8's registered autoGrow
    // meta can walk Inline → section → page. importLayout can't know
    // parentage; only this reconciler (which sees every host item and its
    // nested id) does. Every shell is made BEFORE any parent is stamped, so a
    // stamp never names a grid that doesn't exist yet.
    for (const { nid, hostGridId, hostItemId, hostType } of entries) {
      if (!map.has(nid)) {
        if (!useGridStore.getState().grids[nid]) {
          const host =
            useGridStore.getState().grids[hostGridId]?.items[hostItemId];
          useGridStore.getState().importLayout(nid, {
            config:
              hostType === INLINE_GRID
                ? inlineNestedConfig(host?.position.w ?? 6)
                : SECTION_NESTED_CONFIG,
            items: [],
          });
        }
        map.set(nid, bindYjsToGrid(ydoc, nid));
        // Undoable widget moves/resizes inside this container's nested grid.
        undoManager.addToScope(ydoc.getMap(`grid:${nid}`));
      }
    }
    for (const { nid, hostGridId, hostItemId } of entries) {
      const nested = useGridStore.getState().grids[nid];
      if (
        nested &&
        (nested.parentGridId !== hostGridId ||
          nested.parentItemId !== hostItemId)
      ) {
        useGridStore.getState().setGridParent({
          gridId: nid,
          parentGridId: hostGridId,
          parentItemId: hostItemId,
        });
      }
    }
    for (const [nid, handle] of map) {
      if (!wantedSet.has(nid)) {
        handle.destroy();
        map.delete(nid);
        // Drop the store grid too. This is LIVE section removal, not draft
        // hygiene: a nested section grid the current page no longer references
        // (a user-removed section) must not linger in the module-singleton
        // store — a stale grid with items bleeds into another doc on any later
        // bind of the same id, and nothing else cleans it up mid-session (the
        // draft-open resetGridStore only runs when a draft opens). See
        // draftGridIsolation.test.ts.
        useGridStore.getState().deleteGrid(nid);
      }
    }
  }, [id, sectionIdsKey, ydoc, readOnly, undoManager]);

  // Destroy all nested-grid bindings on unmount (the reconciliation effect
  // above intentionally has no destroy-all cleanup so it can run per change).
  useEffect(() => {
    const map = nestedBindingsRef.current;
    return () => {
      for (const [nid, handle] of map) {
        handle.destroy();
        // Also drop the store grid, so this draft's nested sections don't
        // linger in the module-singleton store and bleed into the next draft
        // (cross-draft isolation — see the reconcile removed-loop above).
        useGridStore.getState().deleteGrid(nid);
      }
      map.clear();
    };
  }, []);

  // Section blocks render through the shared catalog dispatcher; widgetPreview
  // gates each section's nested-grid editing on zoom/read-only.
  const renderWidget = makeRenderWidget(id, widgetPreview);

  return {
    label,
    slugOverride,
    readOnly,
    lod,
    breakpoint,
    authoringWidth,
    widgetPreview,
    pagePreview,
    isNestTarget,
    nestMode,
    nestBadgeScale,
    editingLabel,
    setEditingLabel,
    draftLabel,
    setDraftLabel,
    startRename,
    commitRename,
    renderWidget,
    menuButton,
    openNodeMenu,
    openSlugEditor,
    editingItemId,
    setEditingItemId,
  };
}
